import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MapPin, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { Button, Badge, Spinner } from '@tendedloop/ui';
import { PriorityBadge } from '@/components/ui/PriorityBadge';
import { AmenityIcon } from '@/components/ui/AmenityIcon';
import { useTaskStore } from '@/stores/taskStore';

function useFormatRelativeTime() {
  const { t } = useTranslation('operator');

  return (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return t('time.justNow');
    if (diffMins < 60) return t('time.minutesAgo', { count: diffMins });
    if (diffMins < 1440) return t('time.hoursAgo', { count: Math.floor(diffMins / 60) });
    return t('time.daysAgo', { count: Math.floor(diffMins / 1440) });
  };
}

// Map priority from API to display format
function mapPriority(priority: string): 'P0' | 'P1' | 'P2' {
  switch (priority) {
    case 'critical': return 'P0';
    case 'high': return 'P1';
    case 'medium': return 'P2';
    case 'low': return 'P2'; // Map low to P2 (no P3 in PriorityBadge)
    default: return 'P2';
  }
}

// Map status to badge variant
function getStatusBadgeVariant(status: string): 'warning' | 'secondary' | 'success' {
  switch (status) {
    case 'NEW': return 'warning';
    case 'IN_PROGRESS': return 'secondary';
    case 'COMPLETED': return 'success';
    default: return 'secondary';
  }
}

export function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['operator', 'common']);
  const formatRelativeTime = useFormatRelativeTime();

  const {
    selectedTask,
    isLoading,
    error,
    fetchTaskById,
    updateTaskStatus,
    clearError,
  } = useTaskStore();

  // Fetch task on mount
  useEffect(() => {
    if (id) {
      fetchTaskById(id);
    }
  }, [id, fetchTaskById]);

  const handleStartTask = async () => {
    if (!id) return;
    try {
      await updateTaskStatus(id, 'IN_PROGRESS');
      navigate(`/tasks/${id}/active`);
    } catch {
      // Error handled by store
    }
  };

  const handleRetry = () => {
    clearError();
    if (id) {
      fetchTaskById(id);
    }
  };

  if (isLoading && !selectedTask) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !selectedTask) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] px-6 text-center">
        <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="h-8 w-8 text-error" />
        </div>
        <h2 className="text-xl font-semibold text-neutral-dark mb-2">
          {t('operator:taskDetail.notFound')}
        </h2>
        <p className="text-neutral-medium mb-6">{error || t('operator:taskDetail.loadError')}</p>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/tasks')}
          >
            {t('operator:taskDetail.backToTasks')}
          </Button>
          <Button
            variant="default"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={handleRetry}
          >
            {t('common:buttons.retry')}
          </Button>
        </div>
      </div>
    );
  }

  const task = selectedTask;
  const amenityTypeSlug = task.amenity.amenityType.icon || task.amenity.amenityType.name.toLowerCase();

  return (
    <div className="flex flex-col h-full min-h-[calc(100vh-80px)]">
      {/* Header */}
      <div className="bg-white border-b border-neutral-light px-4 py-4">
        <button
          onClick={() => navigate('/tasks')}
          className="flex items-center gap-2 text-neutral-medium mb-4 hover:text-neutral-dark transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          {t('operator:taskDetail.backToTasks')}
        </button>
        <div className="flex items-start gap-4">
          <AmenityIcon type={amenityTypeSlug} size="xl" />
          <div>
            <div className="flex items-center gap-2 mb-1">
              <PriorityBadge priority={mapPriority(task.priority)} />
              <Badge variant={getStatusBadgeVariant(task.status)}>
                {t(`status.${task.status.toLowerCase()}`)}
              </Badge>
            </div>
            <h1 className="text-xl font-semibold text-neutral-dark">
              {task.amenity.name}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-6 space-y-4">
        {/* Location */}
        <div className="bg-white border border-neutral-light rounded-xl p-4">
          <div className="flex items-center gap-3 text-neutral-medium mb-2">
            <MapPin className="h-5 w-5" />
            <span className="font-medium">{t('operator:taskDetail.location')}</span>
          </div>
          <p className="text-neutral-dark ms-8">
            {task.amenity.floor.name}, {task.amenity.building.name}
          </p>
        </div>

        {/* Issues */}
        <div className="bg-white border border-neutral-light rounded-xl p-4">
          <div className="flex items-center gap-3 text-neutral-medium mb-3">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">{t('operator:taskDetail.issuesReported')}</span>
          </div>
          <div className="space-y-2 ms-8">
            {task.feedback.issues.map(issue => (
              <div key={issue.id} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-error" />
                <span className="text-neutral-dark">{issue.name}</span>
              </div>
            ))}
          </div>
          {task.feedback?.comment && (
            <div className="mt-4 ms-8 p-3 bg-neutral-light/50 rounded-lg">
              <p className="text-sm text-neutral-medium italic">
                "{task.feedback.comment}"
              </p>
            </div>
          )}
        </div>

        {/* Time Info */}
        <div className="bg-white border border-neutral-light rounded-xl p-4">
          <div className="flex items-center gap-3 text-neutral-medium mb-2">
            <Clock className="h-5 w-5" />
            <span className="font-medium">{t('operator:taskDetail.reported')}</span>
          </div>
          <p className="text-neutral-dark ms-8">
            {formatRelativeTime(task.createdAt)}
          </p>
        </div>
      </div>

      {/* Action Button */}
      <div className="px-4 py-4 bg-white border-t border-neutral-light">
        {task.status === 'COMPLETED' ? (
          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={() => navigate('/tasks')}
          >
            {t('operator:taskDetail.backToTasks')}
          </Button>
        ) : (
          <Button
            variant="default"
            size="lg"
            fullWidth
            onClick={handleStartTask}
            loading={isLoading}
          >
            {task.status === 'IN_PROGRESS' ? t('operator:taskActions.continue') : t('operator:taskActions.start')}
          </Button>
        )}
      </div>
    </div>
  );
}
